<script>
  import { getContext, onMount } from 'svelte';
  import { format } from 'svelte-i18n';
  import { link, useNavigate } from 'svelte-navigator';
  import {
    Breadcrumb,
    BreadcrumbItem,
    Button,
    Col,
    Container,
    Dropdown,
    DropdownItem,
    DropdownMenu,
    DropdownToggle,
    Row
  } from '@sveltestrap/sveltestrap';

  import { BrandWizard } from '@client/components/brand-wizard';
  import { EmptyState } from '@client/components/empty-state';
  import { FilterBar } from '@client/components/filter-bar';
  import { SearchFilter } from '@client/components/filters/search';
  import { SortFilter } from '@client/components/filters/sort';
  import { Loader } from '@client/components/loader';
  import { NoMatches } from '@client/components/no-matches';
  import { getNotificationsContext } from '@client/components/notifications';
  import { Pagination } from '@client/components/pagination';
  import { RenameModal } from '@client/components/rename-modal';
  import { Spinner } from '@client/components/spinner';

  import { BizCardsService } from '@client/services/bizcards';
  import { BizCardsRoutingService } from '@client/services/bizcards-routing';
  import { GroupsService } from '@client/services/groups';

  import { hasBranding } from '@client/stores/brand';
  import { bizStudioSettingsStore } from '@client/stores/page-settings';

  import { QueryParams } from '@client/utils/query-params';
  import { generateUniqueRulename } from '@client/utils/rulenames';

  import { STUDIO_FILTERS } from '@client/enums';

  import { sortCollection } from '@common/utils/arrays';
  import { debounce } from '@common/utils/debounce';
  import { merge } from '@common/utils/objects';

  import NewCardModal from './designer/new-card-modal.svelte';

  const { addNotification } = getNotificationsContext();
  const { basepath } = getContext('config');

  const navigate = useNavigate();
  const redirect = (path) => navigate(path);

  const pageQueryParam = parseInt(QueryParams.get('page'), 10);
  const perPageQueryParam = parseInt(QueryParams.get('perpage'), 10);

  let filters = merge({}, { ...STUDIO_FILTERS }, $bizStudioSettingsStore.filters);

  Object.keys(filters.sorting.options).forEach((key) => {
    filters.sorting.options[key].label = STUDIO_FILTERS.sorting.options[key].label;
  });

  if (!filters.search) {
    filters.search = QueryParams.get('search') || '';
  }

  if (pageQueryParam && pageQueryParam !== filters.paging.page) {
    filters.paging.page = pageQueryParam;
  }

  if (pageQueryParam && perPageQueryParam !== filters.paging.perPage) {
    filters.paging.perPage = perPageQueryParam;
  }

  let showFilters = true;
  let queryResult = [];
  let records = [];
  let otherCards = [];
  let totalRecords = 0;
  let updating = false;
  let showNewBusinessCardModal = false;
  let showBrandWizardModal = false;
  let showRenameModal = false;
  let renameCard = null;
  let originalRecordCount = 0;
  let initialized = false;

  let groups = [];
  let routing = [];

  const onError = (text) => {
    addNotification({
      text,
      type: 'error',
      autohide: 2500,
      position: 'top-right'
    });
  };

  const updateQueryParams = (filters) => {
    $bizStudioSettingsStore.filters = { ...filters };
  };

  const onCreateNew = async () => {
    showNewBusinessCardModal = true;
  };

  const onEdit = (card) => {
    redirect(`edit/${card.rulename}`);
  };

  const getBusinessCard = async (card) => {
    let email = 'adminuser';

    if (card.html) {
      return card.html;
    }

    let getSender = card?.senders[0];

    if (getSender) {
      const username = getSender.username || email;

      if (username !== '*') {
        email = username;
      }
    }

    const data = {
      username: email,
      scratchblockname: card.rulename,
      scratchtemplate: card.htmltemplate,
      scratchparams: card.params,
      params: card.params,
      prependLines: 0,
      testData: {
        testsender: email
      }
    };

    card.loading = true;

    const response = await BizCardsService.preview(data);
    const { rendered } = response;

    card.loading = false;
    card.html = rendered;

    return rendered;
  };

  const onSearchFilterChange = debounce(() => {
    filters.paging.page = 1;
    update({ type: 'filter' });
  }, 250);

  const onSortChange = (selected, key, dir) => {
    filters.sorting.key = key;
    filters.sorting.dir = dir;

    update(true);
  };

  const onChangePage = (page) => {
    filters.paging.page = page;
    update(true);
  };

  const onChangeRecords = (perPage) => {
    filters.paging.perPage = perPage;
    update(true);
  };

  const onClone = async (card) => {
    updating = true;

    const rulename = generateUniqueRulename(card.rulename);

    const response = await BizCardsService.update({
      rulename,
      htmltemplate: card.htmltemplate,
      manifesttemplate: card.manifesttemplate,
      params: card.params,
      status: false,
      _id: null,
      _newObject: true
    });

    updating = false;

    if (response.status === 500) {
      updating = false;
      return onError($format('label.FAILURE.CLONE_BUSINESS_CARD', { values: { rulename: card.rulename } }));
    }

    addNotification({
      text: $format('label.SUCCESS.CLONE', { values: { type: card.rulename } }),
      type: 'success',
      autohide: 2500,
      position: 'top-right'
    });

    setTimeout(() => redirect(`/bizcards/studio/edit/${response.rulename}`), 200);
  };

  const onRenameUpdate = async (card, newName) => {
    const response = await BizCardsService.update({ ...card, rulename: newName });

    if (response?.status >= 400) {
      return addNotification({
        text: $format('label.FAILURE.BUSINESS_CARD_RENAME', { values: { rulename: card.rulename } }),
        type: 'error',
        autohide: 2500,
        position: 'top-right'
      });
    }

    addNotification({
      text: $format('label.SUCCESS.RENAME', { values: { from: card.rulename, to: newName } }),
      type: 'success',
      autohide: 2500,
      position: 'top-right'
    });

    showRenameModal = false;
    update();
  };

  const onDelete = async (card) => {
    const confirmed = confirm(
      $format('label.DELETE_PROMPT', { values: { type: $format('label.BUSINESS_CARD').toLowerCase() } })
    );

    if (confirmed) {
      updating = true;

      const response = await BizCardsService.delete(card);

      if (response.status >= 400) {
        updating = false;
        return onError($format('label.FAILURE.DELETE_BUSINESS_CARD', { values: { rulename: card.rulename } }));
      }

      addNotification({
        text: $format('label.SUCCESS.DELETE', { values: { type: card.rulename } }),
        type: 'success',
        autohide: 2500,
        position: 'top-right'
      });

      records.splice(
        records.findIndex((item) => item.rulename === card.rulename),
        1
      );
      otherCards.splice(
        otherCards.findIndex((item) => item.rulename === card.rulename),
        1
      );

      otherCards = [...otherCards];
      records = [...records];

      if (!records.length && filters.paging.page > 1) {
        filters.paging.page -= 1;
      }

      if (!otherCards.length === 0 && !records.length) {
        originalRecordCount = 0;
      }

      totalRecords = filters.search.length ? recordCount : queryResult.length;

      update(true);
    }
  };

  const update = async (skip) => {
    updating = true;

    if (!skip) {
      otherCards = [];

      const cards = await BizCardsService.getAll();

      if (!initialized) {
        originalRecordCount = cards.length;
      }

      const ids = routing.map(({ blockname, status }) => status && blockname).filter(Boolean);

      cards.forEach((item) => {
        const match = routing.find(({ blockname, sendergroups }) => blockname === item.rulename);
        const sendergroups = groups.filter(({ groupname }) => groupname === match?.sendergroups[0]?.groupname);
        const senders = (sendergroups?.length && sendergroups[0]?.senders) || [];

        item.active = ids.includes(item.rulename);
        item.senders = senders;
      });

      otherCards = [...cards];
    }

    queryResult = [...otherCards];
    records = [...queryResult];

    if (filters.search.length) {
      records = queryResult.filter(
        (item) => JSON.stringify(Object.values(item).join(',')).toLowerCase().indexOf(filters.search.toLowerCase()) > -1
      );
    }

    const recordCount = records.length;
    const sortDirection = filters.sorting.dir === 'asc' ? -1 : 1;

    records = sortCollection(records, filters.sorting.key, sortDirection).slice(
      (filters.paging.page - 1) * filters.paging.perPage,
      filters.paging.page * filters.paging.perPage
    );

    totalRecords = filters.search.length ? recordCount : queryResult.length;

    updating = false;

    initialized = true;
  };

  onMount(async () => {
    updating = true;
    routing = await BizCardsRoutingService.getAll();
    groups = await GroupsService.getAll();

    updating = false;

    updateQueryParams(filters);
    update();
  });

  $: hasBusinessCards = initialized && originalRecordCount > 0;
  $: updateQueryParams(filters);
</script>

<div id="studio" class="page">
  <Loader show={updating} backdrop={true} />
  <div class="page-header">
    <Container fluid>
      <Row>
        <Col md="12">
          <Breadcrumb>
            <BreadcrumbItem>
              <a href="{basepath}/bizcards/studio" use:link>{$format('label.BUSINESS_CARDS')}</a>
            </BreadcrumbItem>
            <BreadcrumbItem active>{$format('label.DESIGNER_STUDIO')}</BreadcrumbItem>
          </Breadcrumb>
        </Col>
      </Row>
      <Row class="flex-align--center">
        <Col xs="6" sm="5" md="7">
          <h1 class="page-title">{$format('label.DESIGNER_STUDIO')}</h1>
        </Col>
        <Col xs="6" sm="7" md="5" class="text-right">
          <Button
            id="setup-branding"
            class="me-2 hide--mobile"
            color="secondary"
            on:click={() => (showBrandWizardModal = true)}
          >
            {$format($hasBranding ? 'label.UPDATE_BRANDING' : 'label.SETUP_BRANDING')}
          </Button>
          <Button id="add-business-card" color="secondary" on:click={onCreateNew}>
            {$format('label.NEW_BUSINESS_CARD')}
          </Button>
        </Col>
      </Row>
    </Container>
  </div>
  <div class="page-content">
    {#if hasBusinessCards}
      <div class="filters">
        <FilterBar bind:show={showFilters}>
          <SearchFilter bind:search={filters.search} onChange={onSearchFilterChange} />
          <SortFilter
            bind:filter={filters.sorting.options}
            bind:sortDirection={filters.sorting.dir}
            onClick={onSortChange}
            sortDirParam="sortDir"
            sortKeyParam="sortKey"
          />
        </FilterBar>
      </div>
    {/if}
    <Container fluid>
      {#if hasBusinessCards}
        {#if records.length}
          <div class="business-cards">
            {#each records as card (card._id)}
              <div class="business-card" on:click={() => onEdit(card)} role="button" tabindex="0">
                <div class="business-card--header">
                  <div class="business-card--rule">
                    <span title={card.rulename} class:active={card.active}>{card.rulename}</span>

                    {#if card.active}
                      <div class="business-card--status">{$format('label.ROUTING')}</div>
                    {/if}
                  </div>

                  <div
                    class="business-card--actions"
                    on:click|preventDefault|stopPropagation
                    role="button"
                    tabindex="0"
                  >
                    <Dropdown>
                      <DropdownToggle tag="div" class="os-icon-more-horizontal" />
                      <DropdownMenu>
                        <DropdownItem
                          on:click={() => {
                            renameCard = card;
                            showRenameModal = true;
                          }}
                        >
                          {$format('label.RENAME')}
                        </DropdownItem>
                        <DropdownItem divider />
                        <DropdownItem on:click={() => onEdit(card)}>{$format('label.EDIT')}</DropdownItem>
                        <DropdownItem on:click={() => onClone(card)}>{$format('label.CLONE')}</DropdownItem>
                        {#if !card.active}
                          <DropdownItem divider />
                          <DropdownItem on:click={() => onDelete(card)}>
                            {$format('label.DELETE')}
                          </DropdownItem>
                        {/if}
                      </DropdownMenu>
                    </Dropdown>
                  </div>
                </div>
                <div class="business-card--preview" on:click={() => onEdit(card)} role="button" tabindex="0">
                  {#await getBusinessCard(card)}
                    <div class="loading">
                      <Spinner show inline />
                    </div>
                  {:then html}
                    {#if card.loading}
                      <Spinner show inline />
                    {:else}
                      <iframe
                        tabindex="-1"
                        style="width: 100%;"
                        scrolling="no"
                        title={card.blockname}
                        srcdoc="<html><body><div style='transform: scale(1,1); transform-origin: left top; -moz-transform: scale(1,1); -moz-transform-origin: left top;'>{html ||
                          ''}</div></body></html>"
                      />
                    {/if}
                  {:catch error}
                    <p style="color: red">{error.message}</p>
                  {/await}
                </div>
              </div>
            {/each}
          </div>
        {:else}
          <div class="mt-3">
            <NoMatches message={$format('label.MESSAGE.NO_MATCHES_BUSINESS_CARDS')} />
          </div>
        {/if}
      {:else if initialized}
        <EmptyState
          icon="square-user"
          heading={$format('label.EMPTY_STATE.BUSINESS_CARD_HEADING')}
          description={$format('label.EMPTY_STATE.BUSINESS_CARD_DESCRIPTION')}
          action={$format('label.EMPTY_STATE.BUSINESS_CARD_ACTION')}
          on:click={onCreateNew}
        />
      {/if}
    </Container>
  </div>

  {#if queryResult.length}
    <div class="page-footer">
      <Pagination
        bind:currentPage={filters.paging.page}
        bind:perPage={filters.paging.perPage}
        bind:totalRecords
        {onChangeRecords}
        {onChangePage}
      />
    </div>
  {/if}
</div>

{#if showNewBusinessCardModal}
  <NewCardModal bind:show={showNewBusinessCardModal} />
{/if}

{#if showBrandWizardModal}
  <BrandWizard bind:show={showBrandWizardModal} />
{/if}

<RenameModal
  bind:show={showRenameModal}
  data={renameCard}
  name={renameCard?.rulename ?? ''}
  existingNames={otherCards.map((c) => c.rulename)}
  duplicateError={$format('label.RENAME_MODAL.ERROR.DUPLICATE_BUSINESS_CARD')}
  onUpdate={onRenameUpdate}
/>

<style lang="scss" src="./studio.scss"></style>
